/**
 * Favorites page showing user's saved sessions
 */
import { Link } from 'react-router-dom';
import { Heart, Loader2 } from 'lucide-react';
import { Alert, AlertDescription, Button } from '@/components/ui';
import { SessionCard } from '@/components/features';
import { useLanguage, useIsAuthenticated } from '@/store';
import { useFavorites } from '@/hooks';
import { getFavoritesTranslation } from '@/utils/translations';
import type { Session, LocalizedSession } from '@/types';
import { ROUTES } from '@/config/routes';

// Union type for session data (hooks now return localized sessions)
type SessionData = Session | LocalizedSession;

export function FavoritesPage() {
  const language = useLanguage();
  const isAuthenticated = useIsAuthenticated();
  const { data: favorites, isLoading, error } = useFavorites();

  // Redirect message for unauthenticated users
  if (!isAuthenticated) {
    return (
      <div className="container py-16 text-center">
        <Heart className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
        <h1 className="text-2xl font-bold mb-4">
          {getFavoritesTranslation('signInToView', language)}
        </h1>
        <p className="text-muted-foreground mb-6">
          {getFavoritesTranslation('signInDesc', language)}
        </p>
        <Button asChild>
          <Link to="/login?redirect=/favorites">
            {getFavoritesTranslation('signIn', language)}
          </Link>
        </Button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="container py-16 flex justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container py-16">
        <Alert variant="destructive">
          <AlertDescription>
            {getFavoritesTranslation('loadError', language)}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container py-4 sm:py-8">
      {/* Header */}
      <div className="mb-4 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold mb-2">
          {getFavoritesTranslation('pageTitle', language)}
        </h1>
        <p className="text-sm sm:text-base text-muted-foreground">
          {getFavoritesTranslation('pageSubtitle', language)}
        </p>
      </div>

      {/* Favorites grid or empty state */}
      {favorites && favorites.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
          {favorites.map((session: SessionData) => (
            <SessionCard key={session.id} session={session} isFavorite />
          ))}
        </div>
      ) : (
        <div className="text-center py-16">
          <Heart className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-xl font-semibold mb-2">
            {getFavoritesTranslation('noFavorites', language)}
          </h2>
          <p className="text-muted-foreground mb-6">
            {getFavoritesTranslation('noFavoritesDesc', language)}
          </p>
          <Button asChild>
            <Link to={ROUTES.guided}>
              {getFavoritesTranslation('browseSessions', language)}
            </Link>
          </Button>
        </div>
      )}
    </div>
  );
}
